/**
 * Общие элементы новой редакции раздела «Проекты» (US-26): выбор
 * языковой версии текстов данных и атомы вывода значений.
 *
 * Главное правило раздела — **три разных состояния**: отсутствие
 * значения (`null`) показывается подписью «нет данных», настоящий нуль —
 * как «0», а выключенный супер-администратором виджет не рендерится
 * вовсе (FR-8.1 / FR-8.2 / FR-8.3). Поэтому `null` нигде не подменяется
 * прочерком и не приводится к нулю.
 */
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { barWidth, moneyMln, pctInt } from './format';
import { cn } from '../../../lib/cn';
import type { LocalizedText } from '../../../types/projects';

/**
 * Текст данных на языке интерфейса; отсутствующая языковая версия
 * подменяется другой (US-27 FR-7.3).
 */
export const useLocalizedText = (): ((
  text: LocalizedText | null | undefined,
) => string | null) => {
  const { i18n } = useTranslation();
  const isKz = i18n.language === 'kz';
  // Ссылка стабильна между рендерами: результат уходит в зависимости
  // useMemo вызывающих компонентов.
  return useCallback(
    (text) => {
      if (text === null || text === undefined) return null;
      return (isKz ? (text.kz ?? text.ru) : (text.ru ?? text.kz)) ?? null;
    },
    [isKz],
  );
};

/** Отсутствие значения — подпись, а не прочерк и не нуль (FR-8.1). */
export const NoData = ({ className }: { className?: string }) => {
  const { t } = useTranslation();
  return (
    <span className={cn('lowercase text-text-muted', className)}>
      {t('common.noData')}
    </span>
  );
};

/** Денежная ячейка в млн ₸: единица подписана в заголовке колонки (FR-5.1). */
export const MoneyMln = ({ value }: { value: number | null }) =>
  value === null ? <NoData /> : <>{moneyMln(value)}</>;

/**
 * Полоса освоения и процент (FR-5.6): выше 100 % полоса заполнена
 * целиком и за границу не выходит, значение процента выводится как есть.
 */
export const DevelopmentBar = ({
  pct,
  testId,
}: {
  pct: number | null;
  testId?: string;
}) => (
  <span className="flex items-center justify-end gap-2" data-testid={testId}>
    <span className="h-2 w-16 shrink-0 overflow-hidden rounded-full bg-surface-muted">
      <span
        className="block h-full rounded-full bg-brand-500"
        style={{ width: barWidth(pct) }}
      />
    </span>
    <b className="w-11 shrink-0 text-right tabular-nums">
      {pct === null ? <NoData /> : pctInt(pct)}
    </b>
  </span>
);
